import { metals, currencies } from './pricePages';

export const weightUnits = [
  {
    label: 'Gram (g)',
    value: 'g',
    seoModifier: 'per-gram',
    grams: 1,
    group: 'Metric'
  },
  {
    label: 'Kilogram (kg)',
    value: 'kg',
    seoModifier: 'per-kg',
    grams: 1000,
    group: 'Metric'
  },
  {
    label: 'Troy Ounce (oz t)',
    value: 'ozt',
    seoModifier: 'per-ounce',
    grams: 31.1034768,
    group: 'Precious Metals'
  },
  {
    label: 'Pennyweight (dwt)',
    value: 'dwt',
    seoModifier: 'per-dwt',
    grams: 1.55517384,
    group: 'Precious Metals'
  },
  {
    label: 'Carat (ct)',
    value: 'ct',
    seoModifier: 'per-carat',
    grams: 0.2,
    group: 'Jewellery'
  },
  {
    label: 'Tola',
    value: 'tola',
    seoModifier: 'per-tola',
    grams: 11.6638038,
    group: 'Regional'
  },
  {
    label: 'Tael',
    value: 'tael',
    seoModifier: 'per-tael',
    grams: 37.429,
    group: 'Regional'
  },
  {
    label: 'Grain (gr)',
    value: 'grain',
    seoModifier: 'per-grain',
    grams: 0.06479891,
    group: 'Legacy'
  },
  {
    label: 'Baht (Thailand)',
    value: 'baht',
    seoModifier: 'per-baht',
    grams: 15.244,
    group: 'Regional'
  },
  {
    label: 'Mace',
    value: 'mace',
    seoModifier: 'per-mace',
    grams: 3.779,
    group: 'Regional'
  },
  {
    label: 'Chi',
    value: 'chi',
    seoModifier: 'per-chi',
    grams: 37.799,
    group: 'Regional'
  }
];

export const getWeightUnitByModifier = (modifier) => (
  weightUnits.find((unit) => unit.seoModifier === modifier)
);

const unitModifiers = weightUnits.reduce((config, unit) => ({
  ...config,
  [unit.seoModifier]: {
    slug: unit.seoModifier,
    label: `Price Per ${unit.label.replace(/\s*\(.+\)$/, '')} Calculator`,
    unit
  }
}), {});

export const calculatorModifiers = {
  scrap: {
    slug: 'scrap',
    label: 'Scrap Calculator'
  },
  karat: {
    slug: 'karat',
    label: 'Karat Calculator'
  },
  ...unitModifiers
};

const metalSlugMap = {
  gold: 'XAU',
  silver: 'XAG',
  platinum: 'XPT',
  palladium: 'XPD'
};

const normalizeMetalCode = (metal) => {
  const value = (metal || '').toLowerCase();
  return metalSlugMap[value] || value.toUpperCase();
};

const getModifierCopy = (page) => {
  const metal = page.metal.name;
  const metalLower = metal.toLowerCase();
  const currencyCode = page.currency.code;
  const currencyName = page.currency.name;
  const pair = `${page.metal.code}/${currencyCode}`;

  const copy = {
    scrap: {
      title: `${metal} Scrap Calculator in ${currencyCode} | Live Scrap ${metal} Value`,
      h1: `${metal} Scrap Calculator in ${currencyName}`,
      description: `Calculate scrap ${metalLower} value in ${currencyName} using live ${pair} spot prices, weight, purity, and dealer margin.`,
      intro: `Estimate scrap ${metalLower} value in ${currencyName} from spot price, weight, purity, quantity, and dealer discount.`
    },
    karat: {
      title: `${metal} Karat Calculator in ${currencyCode} | 24K, 22K, 18K ${metal} Value`,
      h1: `${metal} Karat Calculator in ${currencyName}`,
      description: `Calculate ${metalLower} value by karat purity, including 24K, 22K, 21K, 18K, 14K, and 10K values using live ${pair} prices.`,
      intro: `Compare common karat purity values and estimate ${metalLower} value using the ${pair} spot price.`
    },
    'per-gram': {
      title: `${metal} Price Per Gram Calculator in ${currencyCode}`,
      h1: `${metal} Price Per Gram Calculator`,
      description: `Calculate ${metalLower} price per gram in ${currencyName} using the live ${pair} spot price per troy ounce.`,
      intro: `Convert the ${pair} spot price into price per gram and estimate value from weight and purity.`
    },
    'per-ounce': {
      title: `${metal} Price Per Ounce Calculator in ${currencyCode}`,
      h1: `${metal} Price Per Ounce Calculator`,
      description: `Calculate ${metalLower} price per troy ounce in ${currencyName} and estimate value by weight, purity, and quantity.`,
      intro: `Estimate ${metalLower} value from troy ounce weight, purity, quantity, and margin.`
    },
    'per-kg': {
      title: `${metal} Price Per Kilogram Calculator in ${currencyCode}`,
      h1: `${metal} Price Per Kilogram Calculator`,
      description: `Calculate ${metalLower} price per kilogram in ${currencyName} using the live ${pair} spot price and unit conversions.`,
      intro: `Convert spot prices into kilogram values and estimate larger ${metalLower} holdings or inventory.`
    }
  };

  return copy[page.pathModifier];
};

export const calculatorPath = (metal, currency, modifier) => (
  `/calculator/${metal}/${currency}${modifier ? `/${modifier}` : ''}`
);

export const getCalculatorPage = (params) => {
  const metalCode = normalizeMetalCode(params.metal);
  const currencyCode = (params.currency || '').toUpperCase();
  const pathModifier = params.modifier || null;
  const modifierConfig = pathModifier ? calculatorModifiers[pathModifier] : null;
  const metal = metals[metalCode];
  const currency = currencies[currencyCode];

  if (!metal || !currency || (pathModifier && !modifierConfig)) {
    return null;
  }

  const page = {
    metal,
    currency,
    modifier: pathModifier || 'calculator',
    pathModifier,
    modifierConfig
  };
  const pair = `${metal.code}/${currency.code}`;
  const metalLower = metal.name.toLowerCase();
  const modifierCopy = pathModifier ? getModifierCopy(page) : null;

  return {
    ...page,
    pair,
    canonicalUrl: `https://www.goldapi.io${calculatorPath(metal.code, currency.code, pathModifier)}`,
    title: modifierCopy ? modifierCopy.title : `${metal.name} Calculator in ${currency.code} | Live ${pair} Price Calculator`,
    h1: modifierCopy ? modifierCopy.h1 : `${metal.name} Calculator in ${currency.name} (${pair})`,
    description: modifierCopy ? modifierCopy.description : `Calculate live ${metalLower} value in ${currency.name} using real-time ${pair} spot prices. Convert grams, ounces, kilograms, and purity values with GoldAPI.io.`,
    intro: modifierCopy ? modifierCopy.intro : `Calculate live ${metalLower} value in ${currency.name} with weight, purity, quantity, and dealer margin inputs.`
  };
};

const makeCalculatorLink = (label, metal, currency, modifier) => ({
  label,
  to: calculatorPath(metal, currency, modifier)
});

const uniqueLinks = (links) => {
  const seen = {};
  return links.filter((link) => {
    if (seen[link.to]) {
      return false;
    }

    seen[link.to] = true;
    return true;
  });
};

export const popularFooterCalculatorLinks = [
  makeCalculatorLink('Gold Calculator', 'XAU', 'USD'),
  makeCalculatorLink('Silver Calculator', 'XAG', 'USD'),
  makeCalculatorLink('Gold Scrap Calculator', 'XAU', 'USD', 'scrap'),
  makeCalculatorLink('Gold Karat Calculator', 'XAU', 'USD', 'karat'),
  makeCalculatorLink('Gold Calculator AUD', 'XAU', 'AUD'),
  makeCalculatorLink('Silver Calculator AUD', 'XAG', 'AUD')
];

export const homepageCalculatorLinks = [
  makeCalculatorLink('Gold Calculator', 'XAU', 'USD'),
  makeCalculatorLink('Silver Calculator', 'XAG', 'USD'),
  makeCalculatorLink('Gold Scrap Calculator', 'XAU', 'USD', 'scrap'),
  makeCalculatorLink('Gold Karat Calculator', 'XAU', 'USD', 'karat')
];

export const getRelatedCalculatorLinks = (page) => {
  const metalCode = page.metal.code;
  const currencyCode = page.currency.code;
  const metalName = page.metal.name;
  const currentPath = calculatorPath(metalCode, currencyCode, page.pathModifier);

  const modifierLinks = [
    makeCalculatorLink(`${metalName} Per Gram Calculator`, metalCode, currencyCode, 'per-gram'),
    makeCalculatorLink(`${metalName} Per Ounce Calculator`, metalCode, currencyCode, 'per-ounce'),
    makeCalculatorLink(`${metalName} Per Kilogram Calculator`, metalCode, currencyCode, 'per-kg'),
    makeCalculatorLink(`${metalName} Per Pennyweight Calculator`, metalCode, currencyCode, 'per-dwt'),
    makeCalculatorLink(`${metalName} Per Tola Calculator`, metalCode, currencyCode, 'per-tola'),
    makeCalculatorLink(`${metalName} Per Tael Calculator`, metalCode, currencyCode, 'per-tael'),
    makeCalculatorLink(`${metalName} Scrap Calculator`, metalCode, currencyCode, 'scrap'),
    makeCalculatorLink(`${metalName} Karat Calculator`, metalCode, currencyCode, 'karat')
  ];

  return uniqueLinks(modifierLinks).filter((link) => link.to !== currentPath);
};
